import React, { useState } from 'react';
import { Phone, Mail, MapPin, Instagram, Send, Check, Star } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { useSiteContent } from '@/hooks/useSiteContent';
import { CONTACT } from '@/constants/testIds';
import SEO from '@/components/dubinko/SEO';

export default function ContactPage() {
  const { content } = useSiteContent();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setSending(true); setError('');
    try {
      await api.post('/contact', form);
      setDone(true);
      setForm({ name: '', email: '', message: '' });
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <main>
      <SEO
        title="Kontakt"
        description="Pozovite ili pišite Dubinko timu u Nišu. Brzo odgovaramo."
        path="/kontakt"
      />
      <section className="section-pad aurora-bg grain">
        <div className="container-x">
          <span className="tag-pill">Kontakt</span>
          <h1 className="font-display text-5xl md:text-6xl font-extrabold mt-4 max-w-3xl">Stupite u kontakt</h1>
          <p className="text-slate-600 mt-4 text-lg max-w-2xl">Pozovite nas, pišite nam ili popunite formu – odgovaramo u roku od nekoliko sati.</p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-10 shadow-sm">
            {done ? (
              <div data-testid={CONTACT.success} className="text-center py-10">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <Check className="w-8 h-8" />
                </div>
                <h2 className="font-display text-2xl font-bold mt-4">Poruka poslata!</h2>
                <p className="text-slate-600 mt-2">Hvala. Javljamo se u najkraćem roku.</p>
                <button onClick={() => setDone(false)} className="btn-ghost mt-6 px-5 py-2.5 rounded-full font-semibold">Pošalji još jednu</button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid={CONTACT.form}>
                <h2 className="font-display text-2xl font-bold">Pošaljite poruku</h2>
                <div className="mt-6 space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Ime i prezime</label>
                    <input data-testid={CONTACT.name} required value={form.name}
                      onChange={(e) => setForm({...form, name: e.target.value})}
                      className="w-full px-4 py-3 rounded-2xl border border-slate-300 outline-none focus:border-blue-700" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
                    <input data-testid={CONTACT.email} required type="email" value={form.email}
                      onChange={(e) => setForm({...form, email: e.target.value})}
                      className="w-full px-4 py-3 rounded-2xl border border-slate-300 outline-none focus:border-blue-700" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Poruka</label>
                    <textarea data-testid={CONTACT.message} required rows={5} value={form.message}
                      onChange={(e) => setForm({...form, message: e.target.value})}
                      className="w-full px-4 py-3 rounded-2xl border border-slate-300 outline-none focus:border-blue-700 resize-none" />
                  </div>
                  {error && <div className="px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm">{error}</div>}
                  <button data-testid={CONTACT.submit} type="submit" disabled={sending}
                    className="btn-primary px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 disabled:opacity-60">
                    {sending ? 'Šaljem…' : 'Pošalji poruku'} <Send className="w-4 h-4" />
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="space-y-5">
            <a href={`tel:${content.phone}`} className="block bg-white border border-slate-200 rounded-3xl p-6 hover:border-blue-700 transition">
              <div className="flex items-center gap-3">
                <div className="icon-wrap bg-blue-50 text-blue-700"><Phone className="w-5 h-5" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Telefon</div>
                  <div className="font-display font-bold text-lg">{content.phone}</div>
                </div>
              </div>
            </a>
            <a href={`mailto:${content.email}`} className="block bg-white border border-slate-200 rounded-3xl p-6 hover:border-emerald-600 transition">
              <div className="flex items-center gap-3">
                <div className="icon-wrap bg-emerald-50 text-emerald-700"><Mail className="w-5 h-5" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Email</div>
                  <div className="font-display font-bold text-lg">{content.email}</div>
                </div>
              </div>
            </a>
            <div className="bg-white border border-slate-200 rounded-3xl p-6">
              <div className="flex items-center gap-3">
                <div className="icon-wrap bg-purple-50 text-purple-700"><MapPin className="w-5 h-5" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Lokacija</div>
                  <div className="font-display font-bold text-lg">{content.address}</div>
                </div>
              </div>
            </div>
            <a href={content.instagram} target="_blank" rel="noreferrer"
              className="block bg-gradient-to-br from-purple-700 to-blue-700 text-white rounded-3xl p-6 hover:scale-[1.01] transition">
              <div className="flex items-center gap-3">
                <div className="icon-wrap bg-white/15"><Instagram className="w-5 h-5" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest opacity-80">Instagram</div>
                  <div className="font-display font-bold text-lg">@dubinko_nis_018</div>
                </div>
              </div>
            </a>
          </div>
        </div>

        {/* Google Maps + Google review CTA */}
        <div className="container-x mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-3xl overflow-hidden border border-slate-200 shadow-sm">
            <iframe
              title="Dubinko lokacija na Google Maps"
              src={content.google_maps_embed_url || `https://www.google.com/maps?q=${encodeURIComponent(
                `${content.business_street}, ${content.business_postal} ${content.business_city}`
              )}&output=embed`}
              className="w-full h-[360px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <div className="rounded-3xl border border-slate-200 p-6 bg-white flex flex-col">
            <div className="icon-wrap bg-amber-50 text-amber-600"><Star className="w-5 h-5 fill-amber-500" /></div>
            <h3 className="font-display font-bold text-xl mt-3">Nađi nas na Google-u</h3>
            <p className="text-slate-600 mt-2 text-sm leading-relaxed">
              Ostavi zvezdice i podeli iskustvo — mnogo nam pomaže da nas vidi više komšija u Nišu.
            </p>
            <div className="mt-auto pt-4 space-y-2">
              {content.gbp_url ? (
                <>
                  <a href={content.gbp_url} target="_blank" rel="noreferrer"
                    className="btn-primary w-full text-center px-4 py-2.5 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-2">
                    <MapPin className="w-4 h-4" /> Otvori na Mapama
                  </a>
                  <a href={`${content.gbp_url}${content.gbp_url.includes('?') ? '&' : '?'}review=1`} target="_blank" rel="noreferrer"
                    className="btn-ghost w-full text-center px-4 py-2.5 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-2">
                    <Star className="w-4 h-4" /> Ostavi Google recenziju
                  </a>
                </>
              ) : (
                <p className="text-xs text-slate-500">Google Business Profile još nije povezan — administrator može dodati URL u SEO tabu.</p>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
